import React, { forwardRef, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { Select } from "../Select/Select";
import { useFormContext } from "./FormProvider";
import PropTypes from "prop-types";
import { debounce } from "lodash";

const SelectComponent = forwardRef(({ ...props }, ref) => (
  <Select ref={ref} {...props} />
));

SelectComponent.displayName = "SelectComponent";

export const FormSelect = ({
  name,
  loadOptions,
  onLoadOptionsSuccess,
  onChange,
  options = [],
  showSearch = false,
  remoteValidate,
  validateDelay = 500,
  ...props
}) => {
  // const { control, formConfig, trigger, getValues } = useFormContext();
  const [finalOptions, setFinalOptions] = useState(options);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const {
    control,
    setError,
    getValues,
    trigger,
    formState: { errors },
    formConfig,
  } = useFormContext();

  useEffect(() => {
    const fetchOptions = async () => {
      if (loadOptions) {
        setIsLoading(true);
        try {
          const response = await loadOptions();
          const transformedOptions = onLoadOptionsSuccess
            ? onLoadOptionsSuccess(response.data)
            : response.data;
          setFinalOptions(transformedOptions);
          onChange?.(getValues(name), transformedOptions);
        } catch (error) {
          console.error("Failed to load options:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchOptions();
  }, [loadOptions, onLoadOptionsSuccess]);

  useEffect(() => {
    handleFieldChange(getValues(name));
  }, []);

  const debouncedValidate = React.useMemo(
    () =>
      remoteValidate &&
      debounce(async (value) => {
        if (!value) return;
        console.log("text remote validate", value);

        const isValid = await trigger(name);

        if (!isValid || errors[name]) return;

        // setIsValidating(true);
        try {
          await remoteValidate(value);
        } catch (error) {
          setError(name, {
            type: "remote",
            message: error.message,
          });
        } finally {
          // setIsValidating(false);
        }
      }, validateDelay),
    [name, setError, trigger, errors, remoteValidate, validateDelay]
  );

  const handleFieldChange = React.useCallback(
    (value) => {
      onChange?.(value, finalOptions);
      if (remoteValidate && value) {
        debouncedValidate(value);
      }
    },
    [remoteValidate, debouncedValidate, onChange, finalOptions]
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        // console.log(name, field, error);

        return (
          <SelectComponent
            {...field}
            {...formConfig}
            {...props}
            options={finalOptions}
            loading={isSearching}
            disabled={isLoading || props.disabled}
            popupMatchSelectWidth={false}
            error={!!error}
            helperText={error?.message ? error?.message : props.helperText}
            onChange={(value) => {
              field.onChange(value || "");
              handleFieldChange(value);
            }}
            showSearch={showSearch}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            onSearch={async (search) => {
              if (loadOptions) {
                setIsSearching(true);
                try {
                  const response = await loadOptions(search);
                  const transformedOptions = onLoadOptionsSuccess
                    ? onLoadOptionsSuccess(response.data)
                    : response.data;

                  setFinalOptions(transformedOptions);
                } catch (error) {
                  console.error("Failed to load options:", error);
                } finally {
                  setIsSearching(false);
                }
              }
            }}
          />
        );
      }}
    />
  );
};

FormSelect.propTypes = {
  name: PropTypes.string.isRequired,
  loadOptions: PropTypes.func,
  onLoadOptionsSuccess: PropTypes.func,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    })
  ),
  showSearch: PropTypes.bool,
};
